import * as React from 'react';
import classnames from 'classnames';
import {
  CopyOutlined,
  DeleteOutlined,
  LeftOutlined,
  PasteSquareOutlined,
  PushpinOutlined,
  RightOutlined,
} from '@easyv/react-icons';
import { Dropdown, Input, Menu } from '@easyv/react-components';
import { StateJson } from '@easytwin/runtime';
import { isNil } from '@/utils';
import { DropListMenuOption } from '@/UIGenerator';
import { Polygon1Svg } from '../Polygon1Svg';
import { HoverState, StateTab } from './StateTab';

export interface SortableStateTabsProp {
  dataSource: StateJson[];
  selectKey?: string;
  disablePaste?: boolean;
  allowEmpty?: boolean;
  renamingStateId: string | undefined;
  onTabChange?: (key: string) => void;
  onDeleteState?: (key: string) => void;
  onCopyState?: (key: string) => void;
  onPasteState?: (key: string) => void;
  onSetDefaultState?: (key: string) => void;
  onRenameStateStart: (id: string) => void;
  onRenameStateEnd: (name: string) => void;
  onSortState: (originId: string, toId: string, state: HoverState) => void;
}

export function SortableStateTabs({
  dataSource,
  selectKey,
  renamingStateId,
  allowEmpty = false,
  onTabChange,
  onDeleteState,
  onCopyState,
  onPasteState,
  onSetDefaultState,
  onRenameStateStart,
  onRenameStateEnd,
  onSortState,
}: SortableStateTabsProp) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<(HTMLElement | null)[]>([]);
  const [leftDropList, setLeftDropList] = React.useState<StateJson[]>([]);
  const [rightDropList, setRightDropList] = React.useState<StateJson[]>([]);

  const defaultState = dataSource.find((a) => a.using)?.id;
  const isDefaultState = (item: StateJson<any>, index: number) => {
    if (defaultState === undefined) {
      return index === 0;
    }
    return item.id === defaultState;
  };

  const handleScroll = React.useCallback(
    (stateId: string) => {
      if (!scrollRef.current) return;
      const i = dataSource.findIndex((a) => a.id === stateId);
      if (i === -1) return;

      const itemRef = listRef.current[i];
      if (isNil(itemRef)) return;
      if (!itemRef) return;

      // 自动滚动到视野内
      if (itemRef.offsetLeft < scrollRef.current.scrollLeft) {
        scrollRef.current.scrollTo({
          left: itemRef.offsetLeft,
          behavior: 'smooth',
        });
      } else if (
        itemRef.offsetLeft + itemRef.offsetWidth >
        scrollRef.current.scrollLeft + scrollRef.current.offsetWidth
      ) {
        scrollRef.current.scrollTo({
          left: itemRef.offsetLeft - scrollRef.current.offsetWidth + itemRef.offsetWidth,
          behavior: 'smooth',
        });
      }
    },
    [dataSource],
  );

  const switchToPrev = () => {
    const i = dataSource.findIndex((a) => a.id === selectKey);
    if (i === -1) return;
    if (i - 1 >= 0) {
      onTabChange?.(dataSource[i - 1].id);
    }
  };

  const switchToNext = () => {
    const i = dataSource.findIndex((a) => a.id === selectKey);
    if (i === -1) return;
    if (i + 1 < dataSource.length) {
      onTabChange?.(dataSource[i + 1].id);
    }
  };

  React.useEffect(() => {
    selectKey && handleScroll(selectKey);
    // 避免比如修改状态时也会导致触发这里的逻辑
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectKey]);

  const droplist: DropListMenuOption[] = [
    {
      key: '0',
      label: '设置为默认状态',
      icon: <PushpinOutlined />,
      onSelected: () => {
        selectKey && onSetDefaultState?.(selectKey);
      },
    },
    {
      key: '1',
      label: '复制状态',
      icon: <CopyOutlined />,
      onSelected: () => {
        selectKey && onCopyState?.(selectKey);
      },
    },
    {
      key: '2',
      label: '粘贴状态',
      icon: <PasteSquareOutlined />,
      onSelected: () => {
        selectKey && onPasteState?.(selectKey);
      },
    },
    {
      key: '3',
      label: '删除',
      icon: <DeleteOutlined />,
      visible: allowEmpty ? dataSource.length > 0 : dataSource.length > 1,
      onSelected: () => {
        selectKey && onDeleteState?.(selectKey);
      },
    },
  ];

  React.useEffect(() => {
    const calcHiddenItems = () => {
      const leftHiddenItems = dataSource.filter((_, index) => {
        const node = listRef.current[index];
        if (!node || !scrollRef.current) return false;
        return node.offsetLeft + node.offsetWidth < scrollRef.current.scrollLeft;
      });

      const rightHiddenItems = dataSource.filter((_, index) => {
        const node = listRef.current[index];
        if (!node || !scrollRef.current) return false;
        return node.offsetLeft > scrollRef.current.scrollLeft + scrollRef.current.clientWidth;
      });

      setLeftDropList(leftHiddenItems);
      setRightDropList(rightHiddenItems);
    };

    calcHiddenItems();
    const scrollerNode = scrollRef.current;
    scrollerNode?.addEventListener('scroll', calcHiddenItems);

    return () => {
      scrollerNode?.removeEventListener('scroll', calcHiddenItems);
    };
  }, [dataSource, handleScroll, onTabChange]);

  return (
    <>
      <div className='flex rounded-sm h-7 items-center'>
        <Dropdown
          trigger='hover'
          position='bottom'
          disabled={leftDropList.length === 0}
          droplist={
            <DroplistMenu
              states={leftDropList}
              isDefaultState={isDefaultState}
              onTabChange={onTabChange}
            />
          }
        >
          <div
            className='cursor-pointer  flex justify-center items-center hover:bg-bgColorHover rounded-[10px] w-5 h-5 bg-FFFFFF0A'
            onClick={switchToPrev}
          >
            <LeftOutlined size={12} />
          </div>
        </Dropdown>
        <div ref={scrollRef} className='flex-1 overflow-x-auto no-scroll-container relative'>
          <div className='flex px-[6px]'>
            {dataSource.map((item, index) => {
              const isDefault = isDefaultState(item, index);
              return (
                <StateTab
                  key={item.id}
                  ref={(node) => {
                    listRef.current[index] = node;
                  }}
                  item={item}
                  isDefaultState={isDefault}
                  droplist={droplist}
                  selectedStateId={selectKey}
                  renamingStateId={renamingStateId}
                  onTabChange={onTabChange}
                  onRenameStateEnd={onRenameStateEnd}
                  onRenameStateStart={onRenameStateStart}
                  onSort={onSortState}
                />
              );
            })}
          </div>
        </div>
        <Dropdown
          trigger='hover'
          position='bottom'
          disabled={rightDropList.length === 0}
          droplist={
            <DroplistMenu
              states={rightDropList}
              isDefaultState={isDefaultState}
              onTabChange={onTabChange}
            />
          }
        >
          <div
            className='cursor-pointer  flex justify-center items-center hover:bg-bgColorHover rounded-[10px] w-5 h-5 bg-FFFFFF0A'
            onClick={switchToNext}
          >
            <RightOutlined size={12} />
          </div>
        </Dropdown>
      </div>
    </>
  );
}

function DroplistMenu(props: {
  states: StateJson[];
  isDefaultState: (item: StateJson, index: number) => boolean;
  onTabChange?: (id: string) => void;
}) {
  const { states, isDefaultState, onTabChange } = props;

  return (
    <Menu>
      {states.map((item, index) => {
        return (
          <Menu.Item
            style={{ minWidth: 120 }}
            key={item.id}
            onClick={() => {
              onTabChange?.(item.id);
            }}
          >
            <div className='w-full relative h-8 flex items-center'>
              <div className='h-5 flex items-center relative'>
                <span className='align-middle text-xs'>{item.name}</span>
                {isDefaultState(item, index) && (
                  <Polygon1Svg
                    stroke='#17171A'
                    style={{
                      position: 'absolute',
                      right: -7,
                      top: 0,
                    }}
                  />
                )}
              </div>
            </div>
          </Menu.Item>
        );
      })}
    </Menu>
  );
}
